import type { QueryClient } from '@tanstack/react-query'

import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

export const automationRunStatusSchema = z.enum([
  'accepted',
  'claimed',
  'running',
  'invalidated',
  'completed',
  'failed',
  'interrupted',
  'rejected',
])

export const automationRunRecoveryViewSchema = z.object({
  runId: z.string().min(1),
  status: automationRunStatusSchema,
  disposition: z.enum([
    'preview_accepted',
    'wait_for_run',
    'new_preview_required',
    'run_completed',
    'run_failed',
    'run_interrupted',
    'run_rejected',
  ]),
  reason: z.string().nullable(),
  instruction: z.enum([
    'execute_accepted_preview',
    'poll_running_run',
    'create_new_preview',
    'inspect_completed_run',
    'inspect_failed_run',
    'inspect_interrupted_run',
    'inspect_rejected_run',
  ]),
  requiresNewPreview: z.boolean(),
  waitPollingAllowed: z.boolean(),
})

export type AutomationRunStatus = z.infer<typeof automationRunStatusSchema>
export type AutomationRunRecoveryView = z.infer<typeof automationRunRecoveryViewSchema>

export const automationPreviewOwnershipRecoverySchema = z.object({
  disposition: z.literal('preview_not_owned'),
  instruction: z.literal('create_actor_preview'),
  requiresNewPreview: z.literal(true),
  waitPollingAllowed: z.literal(false),
})
export type AutomationPreviewOwnershipRecovery = z.infer<typeof automationPreviewOwnershipRecoverySchema>

export function automationPreviewOwnershipRecoveryUi(
  recovery: AutomationPreviewOwnershipRecovery,
) {
  return {
    clearAcceptedPreview: recovery.requiresNewPreview,
    executeDisabled: true,
    waitPollingAllowed: recovery.waitPollingAllowed,
    copy: 'This Preview belongs to another actor. Create a new Preview for the current actor before executing.',
  }
}

export function automationRunRecoveryUi(view: AutomationRunRecoveryView) {
  return {
    clearAcceptedPreview: view.requiresNewPreview,
    executeDisabled: view.disposition !== 'preview_accepted',
    waitPollingAllowed: view.waitPollingAllowed,
    copy: automationRunRecoveryCopy(view),
  }
}

export interface AutomationRunRecoveryCacheEntry<TRun> {
  recoveryView: AutomationRunRecoveryView
  run: TRun | null
  source: 'pointer' | 'exact'
}

const terminalAutomationRunStatuses = new Set<AutomationRunStatus>([
  'invalidated',
  'completed',
  'failed',
  'interrupted',
  'rejected',
])

export function automationRunRecoveryQueryKey(runId: string) {
  return ['automation-run-recovery', runId] as const
}

export function mergeAutomationRunRecovery<
  TRun,
>(
  current: AutomationRunRecoveryCacheEntry<TRun> | undefined,
  incoming: AutomationRunRecoveryCacheEntry<TRun>,
): AutomationRunRecoveryCacheEntry<TRun> {
  if (!current) {
    return incoming
  }
  const currentTerminal = terminalAutomationRunStatuses.has(current.recoveryView.status)
  const incomingTerminal = terminalAutomationRunStatuses.has(incoming.recoveryView.status)
  if (currentTerminal) {
    if (!incomingTerminal) {
      return current
    }
    return current.source === 'pointer' && incoming.source === 'exact'
      ? incoming
      : current
  }
  if (incomingTerminal) {
    return incoming
  }
  if (current.source === 'exact' && incoming.source === 'pointer') {
    return current
  }
  return incoming
}

export function seedAutomationRunRecovery(
  queryClient: QueryClient,
  recoveryView: AutomationRunRecoveryView,
) {
  const queryKey = automationRunRecoveryQueryKey(recoveryView.runId)
  return queryClient.setQueryData<AutomationRunRecoveryCacheEntry<never>>(
    queryKey,
    current => mergeAutomationRunRecovery(current, {
      recoveryView,
      run: null,
      source: 'pointer',
    }),
  )
}

export function automationRunRecoveryQueryOptions<
  TRun extends { recoveryView: AutomationRunRecoveryView },
>(
  queryClient: QueryClient,
  runId: string | null,
  readRun: (runId: string) => Promise<TRun>,
) {
  return queryOptions({
    queryKey: automationRunRecoveryQueryKey(runId ?? 'inactive'),
    queryFn: async () => {
      const run = await readRun(runId!)
      const incoming = {
        recoveryView: run.recoveryView,
        run,
        source: 'exact' as const,
      }
      const current = queryClient.getQueryData<AutomationRunRecoveryCacheEntry<TRun>>(
        automationRunRecoveryQueryKey(runId!),
      )
      return mergeAutomationRunRecovery(current, incoming)
    },
    enabled: runId !== null,
    refetchInterval: query => query.state.data?.recoveryView.waitPollingAllowed === true
      ? 2_000
      : false,
  })
}

export function commitExactAutomationRunRecovery<
  TRun extends { recoveryView: AutomationRunRecoveryView },
>(
  queryClient: QueryClient,
  run: TRun,
) {
  const queryKey = automationRunRecoveryQueryKey(run.recoveryView.runId)
  return queryClient.setQueryData<AutomationRunRecoveryCacheEntry<TRun>>(
    queryKey,
    current => mergeAutomationRunRecovery(current, {
      recoveryView: run.recoveryView,
      run,
      source: 'exact',
    }),
  )
}

export function automationRunRecoveryCopy(view: AutomationRunRecoveryView): string {
  const reason = view.reason ? `: ${view.reason}` : ''
  switch (view.instruction) {
    case 'execute_accepted_preview':
      return `Automation run ${view.runId} has an accepted Preview ready to execute.`
    case 'poll_running_run':
      return `Automation run ${view.runId} is ${view.status}. Wait for the owning run and refresh status; this Preview was not invalidated.`
    case 'create_new_preview':
      return `Automation run ${view.runId} is invalidated${reason}. Create a new Preview before executing again.`
    case 'inspect_completed_run':
      return `Automation run ${view.runId} completed. Inspect its durable result; this Preview cannot execute again.`
    case 'inspect_failed_run':
      return `Automation run ${view.runId} failed${reason}. Inspect its durable result; this Preview cannot execute again.`
    case 'inspect_interrupted_run':
      return `Automation run ${view.runId} was interrupted${reason}. Inspect its durable result; this Preview cannot execute again.`
    case 'inspect_rejected_run':
      return `Automation run ${view.runId} was rejected${reason}. Inspect its durable result; this Preview cannot execute again.`
  }
}

export function refetchAutomationRunRecovery<
  TRun extends { recoveryView: AutomationRunRecoveryView },
>(
  queryClient: QueryClient,
  runId: string,
  readRun: (runId: string) => Promise<TRun>,
) {
  return queryClient.fetchQuery(
    automationRunRecoveryQueryOptions(queryClient, runId, readRun),
  )
}
